import React, { useCallback, useEffect, useState } from 'react';
import { useSwipeable } from 'react-swipeable';
import styled from '@emotion/styled';

import Category from './Category';
import { useWindowSize } from '~Hooks';

const Wrapper = styled.div`
  flex: 0 0 160px;
  height: 100%;
  max-width: 160px;
  position: relative;
  z-index: 2;

  &:before {
    content: '';
    position: absolute;
    border-right: 1px solid var(--colorBlueGray1000);
    height: 100%;
    width: 136px;
    top: 0;
    left: 0;
  }
`;

const InnerWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-flow: column nowrap;
  height: 100%;
  overflow: auto;
  padding-bottom: 104px;
  box-sizing: border-box;
  margin-top: 1px;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export interface Props {
  categories: Array<{
    id: string;
    name: string;
    color: string;
  }>;
  selectedCategoryId: string | null;
  handleCategoryClick: (categoryId: string, name: string) => void;
}

const CategoriesList = ({ categories, selectedCategoryId, handleCategoryClick }: Props) => {
  const [isScrolling, setIsScrolling] = useState(false);
  const { height: windowHeight } = useWindowSize();
  const isScrollable = categories.length > (windowHeight < 1024 ? 10 : 14);

  const handleClick = useCallback(
    (id, name) => {
      handleCategoryClick(id, name);
    },
    [selectedCategoryId],
  );

  const handlers = useSwipeable({
    delta: 10,
    onSwiping: ({ dir }) => {
      // Don't handle scroll if not necessary (unscrollable or side swipes)
      if (!isScrollable || dir === 'Left' || dir === 'Right') {
        return;
      }

      setIsScrolling(true);
    },
  });

  let timeout: ReturnType<typeof setTimeout>;
  const onTouchEnd = () => {
    timeout = setTimeout(() => {
      setIsScrolling(false);
    }, 100);
  };

  useEffect(() => {
    return () => {
      clearTimeout(timeout);
    };
  }, []);

  return (
    <Wrapper data-test="product-categories">
      <InnerWrapper {...handlers} onTouchEnd={onTouchEnd}>
        {(categories as any).map((category: any) => (
          <Category
            key={category.id}
            id={category.id}
            name={category.name}
            isScrolling={isScrolling}
            backgroundColor={category.color || undefined}
            active={selectedCategoryId === category.id}
            handleCategoryClick={handleClick}
          />
        ))}
      </InnerWrapper>
    </Wrapper>
  );
};

export default React.memo(CategoriesList);
